import { BarCustomLayerProps, ResponsiveBar } from "@nivo/bar";
import { useWindowSize } from "hooks/useWindowSize";
import {
  BAR_RADIUS,
  EXPENSE_BAR_COLOR,
  EXPENSE_BAR_CROSS_LINE_COLOR,
  INSIGHT_TYPE,
  INSIGHT_TYPE_LABEL,
  REVENUE_BAR_COLOR,
  REVENUE_BAR_CROSS_LINE_COLOR,
} from "constants/booksHomePage";

import { colors } from "constants/colors";
import dayjs from "dayjs";
import { ReactElement, useMemo } from "react";
import {
  BarChartConfig,
  ForecastMetaByDate,
  GenericBarChartData,
} from "../ChartComponents";
import { createBar } from "./CashFlowChart";

const MOM_CONFIG_BASE = {
  colors: {
    bar2: "transparent",
    bar2CrossLine: "transparent",
    line: "transparent",
  },
  labels: { bar2: "", line: "" },
};

const REVENUE_MOM_CONFIG: BarChartConfig = {
  colors: {
    ...MOM_CONFIG_BASE.colors,
    bar1: REVENUE_BAR_COLOR,
    bar1CrossLine: REVENUE_BAR_CROSS_LINE_COLOR,
  },
  labels: { ...MOM_CONFIG_BASE.labels, bar1: INSIGHT_TYPE_LABEL.REVENUE },
};

const EXPENSE_MOM_CONFIG: BarChartConfig = {
  colors: {
    ...MOM_CONFIG_BASE.colors,
    bar1: EXPENSE_BAR_COLOR,
    bar1CrossLine: EXPENSE_BAR_CROSS_LINE_COLOR,
  },
  labels: { ...MOM_CONFIG_BASE.labels, bar1: INSIGHT_TYPE_LABEL.EXPENSE },
};

const formatAxisValue = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (absValue >= 1_000_000)
    return `${sign}$${(absValue / 1_000_000).toFixed(1)}M`;
  if (absValue >= 1_000) return `${sign}$${Math.round(absValue / 1_000)}K`;
  return `${sign}$${absValue}`;
};

type MoMBreakdownChartProps = {
  data: GenericBarChartData[];
  reportType: keyof typeof INSIGHT_TYPE;
  onAmountClick?: (date: string) => void;
  forecastMetaByDate?: ForecastMetaByDate;
};

// Vertical month labels so they don't overlap on narrow screens.
const MOBILE_TICK_ROTATION = -90;

export const MoMBreakdownChart = ({
  data,
  reportType,
  onAmountClick,
  forecastMetaByDate,
}: MoMBreakdownChartProps) => {
  const { isMobile } = useWindowSize();
  const config =
    reportType === INSIGHT_TYPE.REVENUE
      ? REVENUE_MOM_CONFIG
      : EXPENSE_MOM_CONFIG;

  const Bar = useMemo(
    () =>
      createBar(
        config,
        onAmountClick
          ? (_type: "revenue" | "expense", date: string) => onAmountClick(date)
          : undefined,
        forecastMetaByDate
      ),
    [config, onAmountClick, forecastMetaByDate]
  );

  const barValues = data?.map((d) => d.bar1);
  const barMax = Math.max(...barValues, 0);

  const getNiceInterval = (value: number): number => {
    const absValue = Math.abs(value);
    if (absValue <= 0) return 100;
    const magnitude = Math.pow(10, Math.floor(Math.log10(absValue)));
    const normalized = absValue / magnitude;
    if (normalized <= 2) return magnitude;
    if (normalized <= 5) return magnitude * 2;
    return magnitude * 5;
  };

  const tickInterval = getNiceInterval(barMax);
  const scaleMax =
    Math.ceil(barMax / tickInterval) * tickInterval || tickInterval;

  const tickValues: number[] = [];
  for (let i = 0; i <= scaleMax; i += tickInterval) {
    tickValues.push(i);
  }

  return (
    <>
      <div className="t-h-full t-w-full">
        <ResponsiveBar
          padding={0.35}
          borderRadius={BAR_RADIUS}
          data={data}
          keys={["bar1"]}
          indexBy="date"
          margin={{ bottom: 40, top: 10, left: 50, right: 0 }}
          colors={[config.colors.bar1]}
          axisBottom={{
            tickSize: 0,
            tickPadding: 10,
            tickRotation: isMobile ? MOBILE_TICK_ROTATION : 0,
            format: (value) => dayjs(value).format("MMM"),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 10,
            tickValues,
            format: formatAxisValue,
          }}
          enableLabel={false}
          enableGridX={false}
          enableGridY={true}
          gridYValues={tickValues}
          layers={[
            "grid",
            "axes",
            Bar as (
              props: BarCustomLayerProps<GenericBarChartData>
            ) => ReactElement,
            "totals",
            "markers",
            "legends",
            "annotations",
          ]}
          valueScale={{ type: "linear", min: 0, max: scaleMax }}
          theme={{
            grid: {
              line: {
                stroke: colors.neutral[0],
                strokeDasharray: "4 4",
              },
            },
            axis: {
              ticks: {
                text: {
                  fill: colors.text[30],
                  fontSize: 12,
                },
              },
            },
          }}
        />
      </div>
    </>
  );
};
